/**
 * Sprint 6B-15, 6B-16, 6B-17: Handoff UI Components
 * 
 * Components:
 * - ResponsibilityBadge (6B-16): Visual indicator of who owns a lead
 * - HandoffConfirmationModal (6B-17): Confirmation dialog for critical transitions
 * - HandoffNotificationToast (6B-15): Toast notifications for handoffs
 */

import React, { useState } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { 
  User, Users, Stethoscope, Building2, AlertTriangle, 
  CheckCircle, XCircle, ArrowRight, Bell, X, Info,
  PartyPopper, Activity
} from 'lucide-react';
import { 
  getResponsibilityInfo, 
  useHandoffNotification,
  RESPONSIBILITY_CONFIG 
} from '../context/HandoffNotificationContext';

// =============================================================================
// 6B-16: RESPONSIBILITY BADGE
// =============================================================================

/**
 * Visual badge showing who is responsible for a lead
 */
export const ResponsibilityBadge = ({ 
  status, 
  assignedName = null,
  size = 'default', // 'sm', 'default', 'lg'
  showPhase = false,
  showDescription = false,
  className = ''
}) => {
  const config = getResponsibilityInfo(status);
  
  // Icon based on owner
  const getIcon = () => {
    switch (config.owner) {
      case 'provider_sales_rep':
        return <User className={size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} />;
      case 'provider_doctor':
        return <Stethoscope className={size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} />;
      case 'provider_admin':
        return <Building2 className={size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} />;
      default:
        return <CheckCircle className={size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} />;
    }
  };

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    default: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };

  return (
    <div className={`inline-flex flex-col gap-1 ${className}`}>
      <div className="flex items-center gap-2">
        <Badge 
          className={`${config.color} border ${sizeClasses[size]} flex items-center gap-1.5`}
          data-testid={`responsibility-badge-${status}`}
        >
          {getIcon()}
          <span>{config.ownerLabel}</span>
          {config.isHandoff && (
            <ArrowRight className={size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} />
          )}
        </Badge>
        {showPhase && (
          <span className="text-xs text-gray-500">{config.phase}</span>
        )}
      </div>
      {assignedName && (
        <span className="text-xs text-gray-600 ml-1">{assignedName}</span>
      )}
      {showDescription && (
        <span className="text-xs text-gray-500 ml-1">{config.description}</span>
      )}
    </div>
  );
};

/**
 * Compact inline responsibility indicator
 */
export const ResponsibilityIndicator = ({ status, className = '' }) => {
  const config = getResponsibilityInfo(status);
  
  const getIcon = () => {
    switch (config.owner) {
      case 'provider_sales_rep':
        return <User className="h-3 w-3" />;
      case 'provider_doctor':
        return <Stethoscope className="h-3 w-3" />;
      case 'provider_admin':
        return <Building2 className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const icon = getIcon();
  if (!icon) return null;

  return (
    <span 
      className={`inline-flex items-center gap-1 text-xs ${config.color.split(' ')[1]} ${className}`}
      title={`Responsible: ${config.ownerLabel}`}
      data-testid={`responsibility-indicator-${status}`}
    >
      {icon}
    </span>
  );
};

// =============================================================================
// 6B-17: HANDOFF CONFIRMATION MODAL
// =============================================================================

/**
 * Confirmation modal for critical status transitions
 */
export const HandoffConfirmationModal = ({ 
  isOpen,
  config,
  leadData,
  onConfirm,
  onCancel
}) => {
  const [note, setNote] = useState('');
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !config) return null;

  const handleConfirm = async () => {
    if (config.requiresNote && !note.trim()) {
      return; // Don't allow confirm without required note
    }
    if (config.checkboxLabel && !checkboxChecked) {
      return; // Don't allow confirm without checkbox
    }
    
    setIsSubmitting(true);
    try {
      await onConfirm(note.trim() || null);
    } finally {
      setIsSubmitting(false);
      setNote('');
      setCheckboxChecked(false);
    }
  };

  const handleCancel = () => {
    setNote('');
    setCheckboxChecked(false);
    onCancel();
  };

  const getSeverityStyles = () => {
    switch (config.severity) {
      case 'danger':
        return {
          icon: <AlertTriangle className="h-6 w-6 text-red-500" />,
          confirmBtn: 'bg-red-600 hover:bg-red-700 text-white',
          border: 'border-red-200'
        };
      case 'warning':
        return {
          icon: <AlertTriangle className="h-6 w-6 text-amber-500" />,
          confirmBtn: 'bg-amber-600 hover:bg-amber-700 text-white',
          border: 'border-amber-200'
        };
      case 'success':
        return {
          icon: <CheckCircle className="h-6 w-6 text-green-500" />,
          confirmBtn: 'bg-green-600 hover:bg-green-700 text-white',
          border: 'border-green-200'
        };
      default:
        return {
          icon: <Info className="h-6 w-6 text-blue-500" />,
          confirmBtn: 'bg-blue-600 hover:bg-blue-700 text-white',
          border: 'border-blue-200'
        };
    }
  };

  const styles = getSeverityStyles();
  const canConfirm = (!config.requiresNote || note.trim()) && 
                     (!config.checkboxLabel || checkboxChecked);

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="handoff-confirmation-modal"
    >
      <Card className={`max-w-md w-full p-0 overflow-hidden border-2 ${styles.border}`}>
        {/* Header */}
        <div className="p-4 border-b flex items-start gap-3">
          {styles.icon}
          <div className="flex-1">
            <h3 className="font-semibold text-lg">{config.title}</h3>
            {leadData && (
              <p className="text-sm text-gray-500 mt-0.5">
                {leadData.patientName} • {leadData.treatment}
              </p>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={handleCancel} className="h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          <p className="text-gray-600">{config.message}</p>

          {/* Optional note input */}
          {config.requiresNote && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {config.noteLabel || 'Note'} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Enter reason..."
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                data-testid="handoff-note-input"
              />
            </div>
          )}

          {/* Optional checkbox */}
          {config.checkboxLabel && (
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={checkboxChecked}
                onChange={(e) => setCheckboxChecked(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                data-testid="handoff-checkbox"
              />
              <span className="text-sm text-gray-600">{config.checkboxLabel}</span>
            </label>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t flex gap-3 justify-end">
          <Button 
            variant="outline" 
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            {config.cancelLabel || 'Cancel'}
          </Button>
          <Button 
            className={styles.confirmBtn}
            onClick={handleConfirm}
            disabled={!canConfirm || isSubmitting}
            data-testid="handoff-confirm-btn"
          >
            {isSubmitting ? 'Processing...' : config.confirmLabel || 'Confirm'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

// =============================================================================
// 6B-15: HANDOFF NOTIFICATION TOAST
// =============================================================================

/**
 * Toast notification for handoff events
 */
export const HandoffNotificationToast = ({ notification, onDismiss }) => {
  if (!notification) return null;

  const getIcon = () => {
    switch (notification.icon) {
      case 'handoff':
        return <ArrowRight className="h-5 w-5 text-amber-500" />;
      case 'treatment':
        return <Activity className="h-5 w-5 text-blue-500" />;
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'celebration':
        return <PartyPopper className="h-5 w-5 text-purple-500" />;
      default:
        return <Bell className="h-5 w-5 text-gray-500" />;
    }
  };

  const getSeverityBorder = () => {
    switch (notification.severity) {
      case 'high':
        return 'border-l-4 border-l-amber-500';
      case 'success':
        return 'border-l-4 border-l-green-500';
      case 'info':
        return 'border-l-4 border-l-blue-500';
      default:
        return 'border-l-4 border-l-gray-300';
    }
  };

  return (
    <div 
      className={`bg-white rounded-lg shadow-lg ${getSeverityBorder()} p-4 max-w-sm animate-slide-in`}
      data-testid={`handoff-notification-${notification.id}`}
    >
      <div className="flex items-start gap-3">
        {getIcon()}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900">{notification.title}</p>
          {notification.patientName && (
            <p className="text-sm text-gray-600 truncate">
              {notification.patientName} • {notification.treatment}
            </p>
          )}
          <p className="text-sm text-gray-500 mt-1">{notification.message}</p>
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-6 w-6 flex-shrink-0"
          onClick={() => onDismiss(notification.id)}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

/**
 * Container for multiple handoff notifications
 */
export const HandoffNotificationContainer = () => {
  const { notifications, dismissNotification } = useHandoffNotification();

  if (notifications.length === 0) return null;

  return (
    <div 
      className="fixed top-4 right-4 z-50 space-y-2"
      data-testid="handoff-notification-container"
    >
      {notifications.map(notification => (
        <HandoffNotificationToast
          key={notification.id}
          notification={notification}
          onDismiss={dismissNotification}
        />
      ))}
    </div>
  );
};

/**
 * Global handoff confirmation modal (uses context)
 */
export const GlobalHandoffConfirmationModal = () => {
  const { pendingConfirmation, closeConfirmation } = useHandoffNotification();

  if (!pendingConfirmation) return null;

  return (
    <HandoffConfirmationModal
      isOpen={true}
      config={pendingConfirmation}
      leadData={pendingConfirmation.leadData}
      onConfirm={pendingConfirmation.onConfirm}
      onCancel={closeConfirmation}
    />
  );
};

// Add animation styles
const styles = `
@keyframes slide-in {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.animate-slide-in {
  animation: slide-in 0.3s ease-out;
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

export default {
  ResponsibilityBadge,
  ResponsibilityIndicator,
  HandoffConfirmationModal,
  HandoffNotificationToast,
  HandoffNotificationContainer,
  GlobalHandoffConfirmationModal
};
